import { geoArea, geoContains, geoDistance, geoGraticule10, geoOrthographic, geoPath } from "d3-geo";
import type { GeoProjection } from "d3-geo";
import type { Feature, MultiPolygon } from "geojson";
import type { Coordinate, GeoData } from "../game/geo.ts";
import type { GlobeLike, GlobeState } from "./play.ts";

const SEA = "#1D2733", LAND = "#55524B", RULE = "#2E3237", INK = "#F2ECE1";

export const isVisible = (point: Coordinate, centre: Coordinate): boolean =>
  geoDistance([point.lon, point.lat], [centre.lon, centre.lat]) < Math.PI / 2 - 0.02;
export const insideDisc = (point: { x: number; y: number }, centre: { x: number; y: number }, radius: number): boolean =>
  Math.hypot(point.x - centre.x, point.y - centre.y) <= radius;
/** A polygon wound the wrong way covers the rest of the sphere: turn only those back (exterior and holes together). */
export const fixWinding = (polygons: number[][][][]): number[][][][] =>
  polygons.map((p) => (geoArea({ type: "Polygon", coordinates: p }) > 2 * Math.PI ? p.map((r) => [...r].reverse()) : p));
export const clampZoom = (z: number): number => Math.min(6, Math.max(1, z));
/** Two fingers that started `startDistance` apart at `startZoom` are now `distance` apart. */
export const pinchZoom = (startZoom: number, startDistance: number, distance: number): number =>
  startDistance > 0 ? clampZoom(startZoom * (distance / startDistance)) : clampZoom(startZoom);
/**
 * A wheel turn or trackpad scroll: up zooms in. Lines and pages count as pixels, and a trackpad
 * pinch (which browsers send as a wheel with ctrlKey and small steps) zooms faster per step.
 */
export function wheelZoom(zoom: number, deltaY: number, deltaMode: number, pinch: boolean): number {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  return clampZoom(zoom * Math.exp(-pixels * (pinch ? 0.01 : 0.002)));
}

type CountryFeature = Feature<MultiPolygon, { id: string }>;

/** The play globe on a canvas: drag to turn, pinch, scroll or buttons to zoom, tap to pick, a label on the pick. */
export class Globe implements GlobeLike {
  private readonly host: HTMLElement;
  private readonly geo: GeoData;
  private readonly onPick: (id: string | null) => void;
  private readonly reducedMotion: boolean;
  private readonly canvas: HTMLCanvasElement;
  private readonly label: HTMLDivElement;
  private readonly features: CountryFeature[];
  private centre: Coordinate = { lon: 10, lat: 25 };
  private zoom = 1;
  private state: GlobeState = { colours: new Map(), dimmedOutside: null, outlined: null, label: null };
  private frame = 0;
  /** The app's glow round the latest guess (or the answer): bright at once, then fading. */
  private glow: { id: string; strength: number } | null = null;
  private glowFrame = 0;
  private readonly resize = new ResizeObserver(() => this.draw());

  constructor(
    host: HTMLElement,
    shapes: Record<string, number[][][][]>,
    geo: GeoData,
    onPick: (id: string | null) => void,
    reducedMotion: boolean,
  ) {
    this.host = host;
    this.geo = geo;
    this.onPick = onPick;
    this.reducedMotion = reducedMotion;
    this.features = Object.entries(shapes).map(([id, coordinates]): CountryFeature => ({
      type: "Feature", properties: { id }, geometry: { type: "MultiPolygon", coordinates: fixWinding(coordinates) },
    }));
    this.canvas = document.createElement("canvas");
    this.canvas.setAttribute("aria-hidden", "true"); // decorative: the guess field is the accessible path
    this.label = document.createElement("div");
    this.label.className = "callout";
    this.label.setAttribute("aria-hidden", "true");
    this.label.hidden = true;
    const zooms = document.createElement("div");
    zooms.className = "zooms";
    zooms.append(this.button("+", "Zoom in", 1.5), this.button("−", "Zoom out", 1 / 1.5));
    host.append(this.canvas, this.label, zooms);
    this.listen();
    this.resize.observe(host);
  }

  setState(s: GlobeState): void {
    this.state = s;
    this.draw();
  }

  turnTo(target: Coordinate, animate: boolean): void {
    cancelAnimationFrame(this.frame);
    const from = { ...this.centre };
    const to = { lon: from.lon + (((target.lon - from.lon + 540) % 360) - 180), lat: Math.max(-60, Math.min(60, target.lat)) };
    if (!animate || this.reducedMotion) { this.centre = to; this.draw(); return; }
    const start = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / 600);
      const e = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
      this.centre = { lon: from.lon + (to.lon - from.lon) * e, lat: from.lat + (to.lat - from.lat) * e };
      this.draw();
      if (t < 1) this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }

  /** A cream outline round `id` that fades out over 0.6 s, as on the app. None with reduced motion. */
  pulse(id: string): void {
    cancelAnimationFrame(this.glowFrame);
    if (this.reducedMotion) { this.glow = null; return; }
    this.glow = { id, strength: 1 };
    this.draw();
    const start = performance.now() + 80;  // held for a moment, then eases out
    const step = (now: number): void => {
      const t = Math.max(0, Math.min(1, (now - start) / 600));
      this.glow = t < 1 ? { id, strength: (1 - t) ** 2 } : null;
      this.draw();
      if (t < 1) this.glowFrame = requestAnimationFrame(step);
    };
    this.glowFrame = requestAnimationFrame(step);
  }

  destroy(): void {
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.glowFrame);
    this.resize.disconnect();
  }

  private projection(): GeoProjection {
    const { width, height } = this.host.getBoundingClientRect();
    return geoOrthographic()
      .rotate([-this.centre.lon, -this.centre.lat])
      .translate([width / 2, height / 2])
      .scale((Math.min(width, height) / 2 - 4) * this.zoom)
      .clipAngle(90);
  }

  private draw(): void {
    const { width, height } = this.host.getBoundingClientRect();
    const ratio = devicePixelRatio || 1;
    if (this.canvas.width !== Math.round(width * ratio)) this.canvas.width = Math.round(width * ratio);
    if (this.canvas.height !== Math.round(height * ratio)) this.canvas.height = Math.round(height * ratio);
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const projection = this.projection();
    const path = geoPath(projection, ctx);
    ctx.beginPath(); path({ type: "Sphere" }); ctx.fillStyle = SEA; ctx.fill();
    ctx.beginPath(); path(geoGraticule10()); ctx.strokeStyle = "rgba(242,236,225,0.07)"; ctx.lineWidth = 0.6; ctx.stroke();
    for (const f of this.features) {
      const id = f.properties.id;
      const country = this.geo.country(id);
      const dimmed = this.state.dimmedOutside !== null && country?.continent !== this.state.dimmedOutside;
      ctx.beginPath(); path(f);
      ctx.globalAlpha = dimmed && !this.state.colours.has(id) ? 0.4 : 1;
      ctx.fillStyle = this.state.colours.get(id) ?? LAND;
      ctx.fill("evenodd");
      ctx.globalAlpha = 1;
      ctx.strokeStyle = SEA; ctx.lineWidth = 0.6; ctx.stroke();
    }
    if (this.state.outlined) {
      const f = this.features.find((x) => x.properties.id === this.state.outlined);
      if (f) { ctx.beginPath(); path(f); ctx.strokeStyle = INK; ctx.lineWidth = 1.5; ctx.stroke(); }
    }
    if (this.glow) {
      const f = this.features.find((x) => x.properties.id === this.glow!.id);
      if (f) {
        const s = this.glow.strength;
        ctx.beginPath(); path(f);
        ctx.strokeStyle = `rgba(242,236,225,${0.12 * s})`; ctx.lineWidth = 7; ctx.stroke();
        ctx.strokeStyle = `rgba(242,236,225,${0.8 * s})`; ctx.lineWidth = 2; ctx.stroke();
      }
    }
    ctx.beginPath(); path({ type: "Sphere" }); ctx.strokeStyle = RULE; ctx.lineWidth = 1; ctx.stroke();
    this.placeLabel(projection);
  }

  private placeLabel(projection: GeoProjection): void {
    const country = this.state.outlined ? this.geo.country(this.state.outlined) : undefined;
    if (!country || !this.state.label || !isVisible(country.centroid, this.centre)) { this.label.hidden = true; return; }
    const [x, y] = projection([country.centroid.lon, country.centroid.lat]) ?? [0, 0];
    const { width } = this.host.getBoundingClientRect();
    this.label.textContent = this.state.label;
    this.label.hidden = false;
    this.label.style.left = `${Math.min(Math.max(x, 70), width - 70)}px`;
    this.label.style.top = `${Math.max(y - 8, 30)}px`;
  }

  private button(text: string, label: string, factor: number): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = text;
    b.setAttribute("aria-label", label);
    b.tabIndex = -1; // the globe is optional: keyboard players never need it
    b.addEventListener("click", () => { this.zoom = clampZoom(this.zoom * factor); this.draw(); });
    return b;
  }

  private listen(): void {
    const pointers = new Map<number, { x: number; y: number }>();
    let last: { x: number; y: number } | null = null;
    let active: number | null = null;
    let moved = 0;
    let pinch: { distance: number; zoom: number } | null = null;
    const spread = (): number => {
      const [a, b] = [...pointers.values()];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    };
    const end = (): void => { last = null; active = null; };
    this.canvas.addEventListener("pointerdown", (e) => {
      if (pointers.size >= 2) return; // two fingers pinch; a third is ignored
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.canvas.setPointerCapture(e.pointerId);
      if (pointers.size === 2) {
        // A second finger turns the drag into a pinch, and the gesture is no longer a tap.
        pinch = { distance: spread(), zoom: this.zoom };
        end();
        return;
      }
      active = e.pointerId;
      last = { x: e.clientX, y: e.clientY };
      moved = 0;
    });
    this.canvas.addEventListener("pointermove", (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch) {
        this.zoom = pinchZoom(pinch.zoom, pinch.distance, spread());
        this.draw();
        return;
      }
      if (!last || e.pointerId !== active) return;
      const dx = e.clientX - last.x, dy = e.clientY - last.y;
      moved += Math.abs(dx) + Math.abs(dy);
      last = { x: e.clientX, y: e.clientY };
      const degreesPerPixel = 180 / (Math.PI * this.projection().scale());
      this.centre = { lon: this.centre.lon - dx * degreesPerPixel, lat: Math.max(-85, Math.min(85, this.centre.lat + dy * degreesPerPixel)) };
      cancelAnimationFrame(this.frame);
      this.draw();
    });
    const lift = (e: PointerEvent): void => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = null; // the finger left behind doesn't turn the globe until it's lifted
      if (e.pointerId === active) end();
    };
    this.canvas.addEventListener("pointerup", (e) => {
      if (e.pointerId === active && last && moved < 6) {
        const rect = this.canvas.getBoundingClientRect();
        const projection = this.projection();
        const x = e.clientX - rect.left, y = e.clientY - rect.top;
        const [cx, cy] = projection.translate();
        // invert() clamps points outside the disc onto its edge, so reject them first.
        const point = insideDisc({ x, y }, { x: cx, y: cy }, projection.scale()) ? projection.invert?.([x, y]) : null;
        const hit = point && this.features.find((f) => this.geo.country(f.properties.id)?.selectable && geoContains(f, point));
        this.onPick(hit ? hit.properties.id : null);
      }
      lift(e);
    });
    this.canvas.addEventListener("pointercancel", lift);
    this.canvas.addEventListener("lostpointercapture", lift);
    // The play screen never scrolls, so the wheel is free to zoom the globe.
    this.canvas.addEventListener("wheel", (e) => {
      e.preventDefault();
      this.zoom = wheelZoom(this.zoom, e.deltaY, e.deltaMode, e.ctrlKey);
      this.draw();
    }, { passive: false });
  }
}
